"use client";

// "use client" isla interactiva: IntersectionObserver (carga diferida + pausa
// fuera de vista) y matchMedia (reduced-motion) necesitan DOM. El shell de la
// página sigue siendo server; solo esta hoja es cliente.

// 2026-10-04 ProductFilm — film de producto 28 s (16:9, música y efectos UI)
// junto al copy que lo describe. Empieza pausado; el primer clic reproduce con
// sonido. Patrón poster-first (research 2026 LCP):
// SSR pinta el póster (next/image lazy, ~40-110 KB); el <video> solo se monta al
// acercarse al viewport (rootMargin 250px, preload="none": nada se baja hasta
// el clic en Play) y se
// pausa al salir (una sola instancia, sin polling). prefers-reduced-motion → solo
// póster. Solo existen masters es/en (motion/promo): resto de locales → en
// con caption visible V6.
//
// 2026-10-05: playback begins only after a Play click. That click calls play()
// with sound inside the user gesture; viewport observers pause/resume afterward.
// Play/pause promises stay serialized to avoid AbortError. Play/pause controls
// are keyboard-operable, and WebM failures retry with the matching MP4.
//
// 2026-10-04 RUN13 FASE 5 deltas V6/V7:
// - V6 handoff de idioma: solo hay masters es/en (`lang = es ? es : en`);
//   12/14 locales ven el master en con caption visible
//   t("showcaseVideoLangNote") ("Video in English", traducida en las 14).
// - V7 rendition 720p (media ≤768px, −47/−57% bytes vs 1080p) + data-saver
//   cableado (film-data-saver.ts FASE3 P9: Save-Data/2G → póster, tap =
//   opt-in que monta el <video>) + botón Play sobre el póster diferido.
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Image from "next/image";
import { Play, Pause, Maximize2, X } from "lucide-react";
import { useLocale, useT } from "@/lib/use-i18n";
import { readFilmNetworkHint, shouldDeferFilm } from "./film-data-saver";
import { Reveal } from "./Reveal";
import { retryVideoWithMp4 } from "./video-source-fallback";
export function ProductFilm() {
  const $ = _c(83);
  const t = useT();
  const locale = useLocale();
  const lang = locale === "es" ? "es" : "en";
  const filmLangMismatch = lang !== locale;
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [loadVideo, setLoadVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const controlsVisible = !isPlaying || isHovering || showControls || isBlocked;
  const [videoFailed, setVideoFailed] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const userPausedRef = useRef(true);
  const hideTimerRef = useRef(null);
  const playPromiseRef = useRef(null);
  const mp4FallbackAttemptedRef = useRef(false);
  const posterWebp = `/video/vortixy-promo-${lang}-poster.webp`;
  const [saveData, setSaveData] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isExpanded) {
    t0 = () => {
      if (!isExpanded) {
        return;
      }
      const onKeyDown = e => {
        if (e.key === "Escape") {
          setIsExpanded(false);
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t1 = [isExpanded];
    $[0] = isExpanded;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const fromUserGesture = t3 === undefined ? false : t3;
      const v = videoRef.current;
      if (!v || userPausedRef.current && !fromUserGesture) {
        return;
      }
      if (fromUserGesture) {
        v.muted = false;
        v.defaultMuted = false;
        v.volume = 1;
      }
      if (v.readyState < 2 && !fromUserGesture) {
        try {
          v.load();
        } catch {}
        return;
      }
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      ;
      try {
        const p = v.play();
        playPromiseRef.current = p;
        p.then(() => {
          playPromiseRef.current = null;
          setIsBlocked(false);
        }).catch(err_0 => {
          playPromiseRef.current = null;
          console.warn("[ProductFilm] playback blocked:", err_0);
          setIsBlocked(true);
          setShowControls(true);
        });
      } catch (t4) {
        const err = t4;
        console.warn("[ProductFilm] play() sincr\xF3nico fall\xF3:", err);
        setIsBlocked(true);
        setShowControls(true);
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const safePlay = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const v_0 = videoRef.current;
      if (!v_0) {
        return;
      }
      const pending = playPromiseRef.current;
      if (pending) {
        pending.then(() => v_0.pause()).catch(() => v_0.pause());
      } else {
        v_0.pause();
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const safePause = t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      const el = wrapRef.current;
      if (!el) {
        return;
      }
      const io = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) {
          if (shouldDeferFilm(readFilmNetworkHint())) {
            setSaveData(true);
            io.disconnect();
            return;
          }
          setLoadVideo(true);
          io.disconnect();
        }
      }, {
        rootMargin: "250px"
      });
      io.observe(el);
      return () => io.disconnect();
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== isReady || $[8] !== loadVideo) {
    t6 = () => {
      const el_0 = wrapRef.current;
      if (!el_0 || !loadVideo) {
        return;
      }
      const io_0 = new IntersectionObserver(entries_0 => {
        const inFrame = entries_0[0]?.isIntersecting ?? false;
        if (inFrame) {
          const video = videoRef.current;
          if (!userPausedRef.current && isReady && video?.paused && !playPromiseRef.current) {
            safePlay();
          }
        } else {
          safePause();
        }
      }, {
        threshold: 0.35
      });
      io_0.observe(el_0);
      return () => io_0.disconnect();
    };
    t7 = [loadVideo, isReady, safePlay, safePause];
    $[7] = isReady;
    $[8] = loadVideo;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => {
        const v_1 = videoRef.current;
        if (v_1 && !v_1.paused) {
          setShowControls(false);
        }
      }, 2400);
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const scheduleHideControls = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setShowControls(true);
      scheduleHideControls();
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleUserActivity = t9;
  let t10;
  if ($[13] !== loadVideo || $[14] !== videoFailed) {
    t10 = () => {
      if (videoFailed) {
        return;
      }
      if (!loadVideo) {
        userPausedRef.current = false;
        setIsBlocked(false);
        flushSync(() => setLoadVideo(true));
        const video_0 = videoRef.current;
        if (!video_0) {
          userPausedRef.current = true;
          return;
        }
        safePlay(true);
        scheduleHideControls();
        return;
      }
      const v_2 = videoRef.current;
      if (!v_2) {
        return;
      }
      if (v_2.paused) {
        userPausedRef.current = false;
        setIsBlocked(false);
        safePlay(true);
        scheduleHideControls();
      } else {
        userPausedRef.current = true;
        safePause();
      }
    };
    $[13] = loadVideo;
    $[14] = videoFailed;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const togglePlay = t10;
  const base = `/video/vortixy-promo-${lang}-1080`;
  const base720 = `/video/vortixy-promo-${lang}-720`;
  let t11;
  if ($[16] !== isPlaying || $[17] !== t) {
    t11 = isPlaying ? t("showcaseVideoPause") : t("showcaseVideoPlay");
    $[16] = isPlaying;
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const playLabel = t11;
  const hiddenOnTouch = isPlaying ? "" : "[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100";
  let t12;
  if ($[19] !== t) {
    t12 = t("showcaseVideoHeading");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <h2 id="product-film-heading" className="text-balance text-[var(--step-3)] font-bold tracking-tight text-foreground">{t12}</h2>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t) {
    t14 = t("showcaseVideoLabel");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <p className="mx-auto mt-3 max-w-[58ch] text-sm leading-relaxed text-muted-foreground sm:text-[15px]">{t14}</p>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t13 || $[28] !== t15) {
    t16 = <Reveal className="mx-auto max-w-[52rem] text-center">{t13}{t15}</Reveal>;
    $[27] = t13;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event => {
      if (event.pointerType !== "touch") {
        setIsHovering(true);
      }
    };
    t18 = event_0 => {
      if (event_0.pointerType !== "touch") {
        setIsHovering(false);
      }
    };
    $[30] = t17;
    $[31] = t18;
  } else {
    t17 = $[30];
    t18 = $[31];
  }
  let t19;
  if ($[32] !== togglePlay) {
    t19 = event_1 => {
      if (event_1.key === " " || event_1.key === "k" || event_1.key === "K") {
        event_1.preventDefault();
        togglePlay();
      }
    };
    $[32] = togglePlay;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t) {
    t20 = t("showcaseVideoLabel");
    $[34] = t;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      aspectRatio: "16 / 9"
    };
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== base || $[38] !== base720 || $[39] !== loadVideo || $[40] !== posterWebp || $[41] !== t || $[42] !== videoFailed) {
    t22 = loadVideo && !videoFailed ? <video ref={videoRef} className="pointer-events-none absolute inset-0 h-full w-full object-cover" loop={true} playsInline={true} preload="none" poster={posterWebp} aria-label={t("showcaseVideoLabel")} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onWaiting={() => setShowControls(true)} onPlaying={() => {
      setIsPlaying(true);
      scheduleHideControls();
    }} onCanPlay={() => {
      setIsReady(true);
      if (!userPausedRef.current && videoRef.current?.paused && !playPromiseRef.current) {
        safePlay();
      }
    }} onCanPlayThrough={() => setIsReady(true)} onError={event_2 => {
      const video_1 = event_2.currentTarget;
      if (event_2.target !== event_2.currentTarget && !video_1.error) {
        return;
      }
      const failedSource = video_1.currentSrc;
      const mediaError = video_1.error;
      if (retryVideoWithMp4(video_1, mp4FallbackAttemptedRef)) {
        console.warn("[ProductFilm] WebM playback failed; retrying matching MP4", {
          src: failedSource,
          code: mediaError?.code ?? null,
          message: mediaError?.message ?? null
        });
        return;
      }
      if (mp4FallbackAttemptedRef.current && failedSource.toLowerCase().endsWith(".webm")) {
        return;
      }
      console.warn("[ProductFilm] video unavailable, showing poster fallback", {
        src: failedSource,
        code: mediaError?.code ?? null,
        message: mediaError?.message ?? null
      });
      setVideoFailed(true);
      setIsBlocked(true);
      setShowControls(true);
    }}><source src={`${base720}.webm?v=${"audio-2026-10-24-v2"}`} type="video/webm" media="(max-width: 768px)" /><source src={`${base720}.mp4?v=${"audio-2026-10-24-v2"}`} type="video/mp4" media="(max-width: 768px)" /><source src={`${base}.webm?v=${"audio-2026-10-24-v2"}`} type="video/webm" /><source src={`${base}.mp4?v=${"audio-2026-10-24-v2"}`} type="video/mp4" /></video> : <Image src={posterWebp} alt={t("showcaseVideoLabel")} width={1280} height={720} loading="lazy" fetchPriority="low" decoding="async" sizes="(max-width: 1024px) 90vw, 64rem" style={{
      aspectRatio: "16 / 9"
    }} className="absolute inset-0 h-full w-full object-cover" />;
    $[37] = base;
    $[38] = base720;
    $[39] = loadVideo;
    $[40] = posterWebp;
    $[41] = t;
    $[42] = videoFailed;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== controlsVisible || $[45] !== hiddenOnTouch || $[46] !== isPlaying || $[47] !== loadVideo || $[48] !== playLabel || $[49] !== saveData || $[50] !== togglePlay || $[51] !== videoFailed) {
    t23 = (loadVideo || saveData) && (!videoFailed ? <div className={`absolute inset-0 z-10 flex items-center justify-center bg-black/20 transition-[opacity,transform] duration-300 ${controlsVisible ? "translate-y-0 opacity-100 pointer-events-auto" : `translate-y-1.5 opacity-0 pointer-events-none ${hiddenOnTouch}`} focus-within:translate-y-0 focus-within:opacity-100 focus-within:pointer-events-auto`}><button type="button" onClick={event_3 => {
        event_3.stopPropagation();
        togglePlay();
      }} aria-pressed={isPlaying} aria-label={playLabel} className="relative flex size-16 sm:size-20 items-center justify-center rounded-full border border-white/25 bg-black/65 text-white shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-110 hover:border-white/40 hover:bg-black/85 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"><span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-white/10 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />{isPlaying ? <Pause className="size-8 sm:size-9 fill-current" /> : <Play className="size-8 sm:size-9 fill-current translate-x-0.5" />}</button></div> : null);
    $[44] = controlsVisible;
    $[45] = hiddenOnTouch;
    $[46] = isPlaying;
    $[47] = loadVideo;
    $[48] = playLabel;
    $[49] = saveData;
    $[50] = togglePlay;
    $[51] = videoFailed;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== controlsVisible || $[54] !== hiddenOnTouch || $[55] !== isPlaying || $[56] !== locale || $[57] !== togglePlay || $[58] !== videoFailed) {
    t24 = !videoFailed && <button type="button" onClick={event_4 => {
      event_4.stopPropagation();
      if (isPlaying) {
        togglePlay();
      }
      setIsExpanded(true);
    }} aria-label={locale === "es" ? "Ampliar video" : "Expand video"} title={locale === "es" ? "Ampliar video" : "Expand video"} className={`absolute top-3.5 right-3.5 z-20 flex size-9 sm:size-10 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-black/80 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${controlsVisible ? "translate-y-0 opacity-100 pointer-events-auto" : `-translate-y-1 opacity-0 pointer-events-none ${hiddenOnTouch}`} focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:pointer-events-auto`}><Maximize2 className="size-4 sm:size-4.5" aria-hidden="true" /></button>;
    $[53] = controlsVisible;
    $[54] = hiddenOnTouch;
    $[55] = isPlaying;
    $[56] = locale;
    $[57] = togglePlay;
    $[58] = videoFailed;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t19 || $[61] !== t20 || $[62] !== t22 || $[63] !== t23 || $[64] !== t24 || $[65] !== togglePlay) {
    t25 = <div ref={wrapRef} onPointerEnter={t17} onPointerLeave={t18} onTouchStart={handleUserActivity} onKeyDown={t19} onClick={togglePlay} role="group" aria-label={t20} className="lovable-card group relative cursor-pointer overflow-hidden select-none" style={t21}>{t22}{t23}{t24}</div>;
    $[60] = t19;
    $[61] = t20;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
    $[65] = togglePlay;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== filmLangMismatch || $[68] !== t) {
    t26 = filmLangMismatch && <p className="mt-3 text-center text-xs text-muted-foreground">{t("showcaseVideoLangNote")}</p>;
    $[67] = filmLangMismatch;
    $[68] = t;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== t25 || $[71] !== t26) {
    t27 = <Reveal delay={120} className="mx-auto mt-8 max-w-[64rem] sm:mt-10">{t25}{t26}</Reveal>;
    $[70] = t25;
    $[71] = t26;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t16 || $[74] !== t27) {
    t28 = <div className="mx-auto max-w-container-wide gutter-fluid">{t16}{t27}</div>;
    $[73] = t16;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== base || $[77] !== isExpanded || $[78] !== locale) {
    t29 = isExpanded && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-8 backdrop-blur-md animate-in fade-in-0 duration-200" role="dialog" aria-modal="true" aria-label={locale === "es" ? "Video de producto ampliado" : "Expanded product film"} onClick={() => setIsExpanded(false)}><button type="button" onClick={() => setIsExpanded(false)} aria-label={locale === "es" ? "Cerrar video" : "Close video"} className="absolute top-4 right-4 z-50 flex size-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white shadow-xl backdrop-blur-md transition-all hover:scale-110 hover:bg-white/20 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"><X className="size-6" aria-hidden="true" /></button><div className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/15 bg-black" onClick={_temp}><video controls={true} autoPlay={true} playsInline={true} src={`${base}.mp4?v=${"audio-2026-10-24-v2"}`} className="size-full object-contain"><source src={`${base}.webm?v=${"audio-2026-10-24-v2"}`} type="video/webm" /><source src={`${base}.mp4?v=${"audio-2026-10-24-v2"}`} type="video/mp4" /></video></div></div>;
    $[76] = base;
    $[77] = isExpanded;
    $[78] = locale;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== t28 || $[81] !== t29) {
    t30 = <section id="product-film" className="landing-section relative overflow-hidden" aria-labelledby="product-film-heading">{t28}{t29}</section>;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  return t30;
}
function _temp(event_5) {
  return event_5.stopPropagation();
}